import React from 'react'
import Label from '../../../components/common/form/label/Label'
import { t, Trans } from '@lingui/macro'
import { Box, ButtonPrimary, Input } from '../../../components'
import { useLoginContext } from '../login/LoginContext'
import IconArrowRight from '../../../components/common/icon/IconArrowRight'
import { notification } from '../../../components/common/notifications/Notifications'
import emailRegex from '../../../app/services/emailRegex'
import { ForgotPasswordLink } from '../login/components/ForgotPasswordLink'
import Form from '../../../components/common/form/Form'
import { WelcomeMessage } from '../login/components/WelcomeMessage'
import { OauthLoginButtons } from '../components/OauthLoginButtons'
import { SignToDifferentTeamLink } from '../login/components/SignToDifferentTeamLink'
import { observer } from 'mobx-react'
import { useAsyncCallback } from '../useAsyncCallback'

type LoginStepEmailProps = {
  submit: () => Promise<void>
}
export const LoginStepEmailForm: React.FC<LoginStepEmailProps> = observer(
  ({ submit }) => {
    const ctx = useLoginContext()

    const [handleSubmit, pending] = useAsyncCallback(async () => {
      const isEmail = ctx.userIdentity && !!emailRegex.exec(ctx.userIdentity)

      if (!ctx.userIdentity || !(isEmail || ctx.isAccountNameLoginOn)) {
        notification.warning(t`Please insert valid email`)
        return
      }
      await submit()
    })

    const isOnlySSOLogin = !!ctx.organisation?.only_sso_login

    return (
      <Form onSubmit={handleSubmit}>
        <WelcomeMessage />
        {!isOnlySSOLogin && (
          <>
            <Box>
              <Label htmlFor="inputEmail">
                <Trans>
                  {ctx.isAccountNameLoginOn
                    ? t`Account name`
                    : t`Email address`}
                </Trans>
              </Label>
              <Input
                type={ctx.isAccountNameLoginOn ? 'text' : 'email'}
                placeholder={
                  ctx.isAccountNameLoginOn ? t`Account name` : 'you@email.com'
                }
                id="inputEmail"
                autoComplete={ctx.isAccountNameLoginOn ? 'username' : 'email'}
                autoFocus
                onChange={(e) => {
                  const value = e.target.value
                  ctx.setUserIdentity(
                    ctx.isAccountNameLoginOn ? value : value.toLowerCase()
                  ) // if account name is enabled users who input email must use lower cased email-we won't lower case their input as we do for orgs without account_name feature
                }}
              />
            </Box>
            <ButtonPrimary
              type="submit"
              mt="32px"
              disabled={pending}
              isLoading={pending}
              isFullWidth
              rightIcon={<IconArrowRight />}
            >
              <span className="btn__text">
                <Trans>Next</Trans>
              </span>
            </ButtonPrimary>
          </>
        )}
        {/* Show forgot password link only when account name login and only sso are off */}
        {!ctx.isAccountNameLoginOn && !isOnlySSOLogin && <ForgotPasswordLink />}

        <OauthLoginButtons
          allowInstantLogin={
            isOnlySSOLogin &&
            !!ctx.organisation?.features?.instant_sso_login &&
            !ctx.organisation?.features?.account_name_login
          }
        />
        <SignToDifferentTeamLink />
      </Form>
    )
  }
)

LoginStepEmailForm.displayName = 'LoginStepEmail'
